<script lang="ts">
    import type { DocumentHeader } from '../types/document-header-footer'
    import type { Company } from '../../../modules/usersApi/companies/company'

    export let header: DocumentHeader
    /** Só é necessário em contexto público (sem usuário logado) — usado para buscar os dados da
     * empresa via API pública quando `header.useCompanyData` está ativo. */
    export let companyId: string | null = null

    let company: Company | null = null
    let loadedForCompanyId: string | null = null

    $: if (header.useCompanyData) loadCompany()

    async function loadCompany() {
        try {
            if (window.store?.company) {
                company = window.store.company
                return
            }
            if (!companyId || loadedForCompanyId === companyId) return
            loadedForCompanyId = companyId
            company = await window.api.users.companies.getPublicCompany(companyId)
        } catch (error) {
            console.error(error)
        }
    }

    function buildAddressLine(c: Company): string {
        if (!c.street) return ''
        let address = c.street
        address += c.addressNumber ? ', ' + c.addressNumber : ', s/n'
        address += c.addressComplement ? ', ' + c.addressComplement : ''
        address += c.neighborhood ? ', ' + c.neighborhood : ''
        address += c.zipCode ? ', ' + c.zipCode : ''
        address += c.city ? ', ' + c.city : ''
        address += c.state ? ' - ' + c.state : ''
        return address
    }

    function buildContactLine(c: Company): string {
        const parts: string[] = []
        if (c.phone) parts.push(window.api.utils.utils.phoneFriendly(c.phone))
        if (c.email) parts.push(c.email)
        return parts.join(' • ')
    }

    $: effectiveHeader =
        header.useCompanyData && company
            ? {
                  ...header,
                  logo: company.logo || null,
                  title: company.companyName || '',
                  subtitle: company.document || '',
                  addressLine: buildAddressLine(company),
                  contactLine: buildContactLine(company),
              }
            : header
</script>

{#if effectiveHeader.enabled}
    <div class="doc-card doc-header">
        <div class="doc-header-row">
            {#if effectiveHeader.logo}
                <img src={effectiveHeader.logo} alt="Logo" class="doc-header-logo" />
            {/if}
            <div class="doc-header-text">
                {#if effectiveHeader.title}<div class="doc-header-title">{effectiveHeader.title}</div>{/if}
                {#if effectiveHeader.subtitle}<div class="doc-header-subtitle">{effectiveHeader.subtitle}</div>{/if}
                {#if effectiveHeader.addressLine}<div class="doc-header-line">{effectiveHeader.addressLine}</div>{/if}
                {#if effectiveHeader.contactLine}<div class="doc-header-line">{effectiveHeader.contactLine}</div>{/if}
            </div>
        </div>
    </div>
{/if}

<style>
    .doc-card {
        /* background: #fff; */
        border-radius: 12px;
        padding: 18px 0px;
        margin-bottom: 14px;
        /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06); */
    }
    .doc-header {
        /* border-top: 4px solid #212529; */
    }
    .doc-header-row {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    /* O logo é enviado pelo usuário em qualquer proporção, e o preview do editor o mostra numa caixa
       fixa — o que esconde o problema até a hora de imprimir. Com altura fixa e largura automática,
       uma imagem em faixa (ex.: 3000x300) calculava uma largura enorme, e `flex-shrink: 0` impedia o
       cabeçalho de contê-la: estourava a folha. Travar os dois eixos com `max-*` e deixar as
       dimensões em `auto` faz qualquer proporção caber na caixa sem distorcer. O `min()` mantém a
       trava proporcional em telas estreitas (link público no celular), onde 240px já seria demais. */
    .doc-header-logo {
        width: auto;
        height: auto;
        max-width: min(240px, 35%);
        max-height: 86px;
        object-fit: contain;
        flex-shrink: 0;
        border-radius: 6px;
    }
    .doc-header-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .doc-header-title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #212529;
    }
    .doc-header-subtitle {
        font-size: 0.85rem;
        font-weight: 500;
        color: #495057;
    }
    .doc-header-line {
        font-size: 0.76rem;
        color: #868e96;
    }
</style>
